
import { cn } from "@/lib/utils";
import { LayoutDashboard, GraduationCap, GamepadIcon, Trophy, PiggyBank, DollarSign, LineChart, Users, UserCircle, LogOut, BookOpen, Award } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";

interface SidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const Sidebar = ({ isOpen, onOpenChange }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState<'learn' | 'invest'>(() => {
    return (localStorage.getItem('navigationMode') as 'learn' | 'invest') || 'learn';
  });
  const [isMobile, setIsMobile] = useState(false);

  // Handle window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const learnLinks = [
    {
      href: "/home",
      label: "Dashboard",
      icon: LayoutDashboard
    },
    {
      href: "/learn",
      label: "Learn",
      icon: GraduationCap
    },
    {
      href: "/play",
      label: "Play",
      icon: GamepadIcon
    },
    {
      href: "/leaderboards",
      label: "Leaderboards",
      icon: Trophy
    },
    {
      href: "/rewards",
      label: "Rewards",
      icon: Award
    },
    {
      href: "/community",
      label: "Community",
      icon: Users
    }
  ];

  const investLinks = [
    {
      href: "/home",
      label: "Dashboard",
      icon: LayoutDashboard
    },
    {
      href: "/invest",
      label: "Invest",
      icon: DollarSign
    },
    {
      href: "/save",
      label: "Save",
      icon: PiggyBank
    },
    {
      href: "/track",
      label: "Track",
      icon: LineChart
    },
    {
      href: "/rewards",
      label: "Rewards",
      icon: Award
    },
    {
      href: "/community",
      label: "Community",
      icon: Users
    }
  ];

  const links = mode === 'learn' ? learnLinks : investLinks;

  // Helper function to check if a link is active
  const isLinkActive = (href: string) => {
    // For exact home route match
    if (href === "/home") {
      return location.pathname === "/home";
    }
    
    // For other routes, check if current path starts with the link href
    // This handles subroutes like /learn/dashboard, /learn/topic/123, etc.
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account."
      });
      navigate("/login");
    } catch (error) {
      toast({
        title: "Error logging out",
        description: "There was a problem logging out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const toggleMode = () => {
    const newMode = mode === 'learn' ? 'invest' : 'learn';
    console.log("Sidebar - Toggling mode from", mode, "to", newMode);
    setMode(newMode);
    localStorage.setItem('navigationMode', newMode);
    window.dispatchEvent(new CustomEvent('navigationModeChange', {
      detail: newMode
    }));
    navigate("/home");
    if (isMobile) onOpenChange(false);
  };

  const handleNavigation = (href: string) => {
    navigate(href);
    if (isMobile) onOpenChange(false);
  };

  const SidebarContent = () => (
    <div className="h-full w-full bg-background flex flex-col">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            centse
          </h2>
          {mode === 'invest' && (
            <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">
              BETA
            </Badge>
          )}
        </div>
        <Button 
          onClick={toggleMode} 
          variant="outline" 
          className={cn(
            "w-full gap-2 transition-all duration-500 ease-in-out transform hover:scale-105",
            "relative overflow-hidden group",
            mode === 'learn' 
              ? 'hover:bg-primary/10 hover:border-primary/30' 
              : 'hover:bg-secondary/10 hover:border-secondary/30',
            "animate-fade-in"
          )}
        >
          <div className={cn(
            "absolute inset-0 transition-transform duration-500 ease-in-out",
            mode === 'learn' ? '-translate-x-full' : 'translate-x-0',
            mode === 'learn' ? 'bg-primary/5' : 'bg-secondary/5'
          )} />
          {mode === 'learn' ? (
            <div className="flex items-center gap-2 relative animate-fade-in">
              <DollarSign className="h-4 w-4 transition-all duration-300 group-hover:scale-110" />
              <span className="font-medium">Switch to Invest</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 relative animate-fade-in">
              <BookOpen className="h-4 w-4 transition-all duration-300 group-hover:scale-110" />
              <span className="font-medium">Switch to Learn</span>
            </div>
          )}
        </Button>
      </div>
      
      <nav className="flex-1 px-3">
        <div className="space-y-1">
          {links.map(link => {
            const Icon = link.icon;
            const isActive = isLinkActive(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => {
                  if (isMobile) onOpenChange(false);
                }}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium",
                  "transition-all duration-300 ease-in-out transform hover:scale-[1.02]",
                  "relative overflow-hidden group",
                  isActive 
                    ? mode === 'learn' 
                      ? "bg-primary text-primary-foreground shadow-sm" 
                      : "bg-secondary text-secondary-foreground shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                )}
              >
                <div className={cn(
                  "absolute inset-0 opacity-0 transition-opacity duration-300",
                  "group-hover:opacity-100",
                  mode === 'learn' ? 'bg-primary/5' : 'bg-secondary/5'
                )} />
                <Icon className={cn(
                  "h-4 w-4 transition-transform duration-300",
                  "group-hover:scale-110",
                  isActive ? "animate-bounce-small" : ""
                )} />
                <span className="relative">{link.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="p-3 mt-auto border-t bg-background/50">
        <Link
          to="/profile"
          onClick={() => {
            if (isMobile) onOpenChange(false);
          }}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-300 ease-in-out transform hover:scale-[1.02] group"
        >
          <UserCircle className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          <span>Profile</span>
        </Link>
        <button 
          onClick={handleLogout} 
          className="flex w-full items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-300 ease-in-out transform hover:scale-[1.02] group"
        >
          <LogOut className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          <span>Log out</span>
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="p-0 w-72">
          <SidebarContent />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="h-screen w-64 bg-background border-r flex flex-col fixed left-0 top-0 shadow-sm hidden md:flex">
      <SidebarContent />
    </div>
  );
};

export default Sidebar;
